// F4: クロスファイル整合チェック（手順書「クロスファイル整合ルール」1〜10 の機械化）
// 結果は エラー（参照切れ）/ 警告 に分類し、ファイル・JSON パス・修正ヒント付きで返す。
import { existsSync, readdirSync } from 'node:fs';
import path from 'node:path';
import { requireGameDir, updateGameMeta } from './games.mjs';
import { readJsonIfExists, walkJson, getByDotPath } from './json-utils.mjs';
import { auditLog } from './logger.mjs';
import { forbiddenShopItemReason } from './shop-policy.mjs';

function finding(level, rule, file, jsonPath, message, hint = '') {
    return { level, rule, file, jsonPath, message, hint };
}

/** 物語生成で登録した敵タイプのうち、全マップを通して静的・動的配置が1件もないID。 */
export function unplacedStoryEnemyTypeIds(enemiesMaster, enemyDocuments) {
    const required = new Set((enemiesMaster?.enemies ?? [])
        .filter((entry) => entry?.storyGenerated === true && typeof entry.id === 'string' && entry.id)
        .map((entry) => entry.id));
    if (required.size === 0) return [];
    const used = new Set();
    for (const document of enemyDocuments ?? []) {
        for (const enemy of document?.enemies ?? []) {
            if (typeof enemy?.type === 'string') used.add(enemy.type);
        }
        for (const spawn of document?.spawnPoints ?? []) {
            const type = spawn?.type ?? spawn?.enemyId;
            if (typeof type === 'string') used.add(type);
        }
    }
    return [...required].filter((id) => !used.has(id)).sort();
}

export function runIntegrityCheck(gameId) {
    const dir = requireGameDir(gameId);
    const mapsDir = path.join(dir, 'maps');
    const findings = [];
    const rel = (p) => path.relative(dir, p).split(path.sep).join('/');

    const shared = (name) => readJsonIfExists(path.join(mapsDir, '_shared', name));
    const mapList = shared('map-list.json')?.maps ?? [];
    const mapDirs = existsSync(mapsDir)
        ? readdirSync(mapsDir, { withFileTypes: true }).filter((e) => e.isDirectory() && e.name !== '_shared').map((e) => e.name)
        : [];

    // マスタ ID 集合
    const itemsJson = shared('items.json');
    const equipmentJson = shared('equipment.json');
    const enemiesMaster = shared('enemies.json');
    const lootJson = shared('loot-tables.json');
    const skillsJson = shared('skills.json');
    const shopMaster = shared('shop-master.json');
    const audioJson = shared('audio.json');
    const jaLocale = readJsonIfExists(path.join(mapsDir, '_shared/i18n/locales/ja.json'));
    const enLocale = readJsonIfExists(path.join(mapsDir, '_shared/i18n/locales/en.json'));

    const itemIds = new Set((itemsJson?.items ?? []).map((i) => i.id));
    const itemById = new Map((itemsJson?.items ?? []).filter((i) => i?.id).map((i) => [i.id, i]));
    const equipItemIds = new Set((equipmentJson?.items ?? []).map((i) => i.id));
    const equipTemplateIds = new Set((equipmentJson?.templates ?? []).map((t) => t.id));
    const enemyMasterIds = new Set((enemiesMaster?.enemies ?? []).map((e) => e.id));
    const lootTableIds = new Set((lootJson?.lootTables ?? []).map((t) => t.id));
    const commonPoolIds = new Set(
        Array.isArray(shopMaster?.commonPools)
            ? shopMaster.commonPools.map((p) => p.id)
            : Object.keys(shopMaster?.commonPools ?? {}),
    );
    const audioTracks = new Set(Object.keys(audioJson?.tracks ?? {}));
    const enemyDocuments = [];

    // ルール 1: map-list ⇔ フォルダ ⇔ meta.id
    for (const id of mapList) {
        if (!mapDirs.includes(id)) {
            findings.push(finding('error', 1, 'maps/_shared/map-list.json', 'maps', `mapId「${id}」のフォルダ maps/${id}/ がありません`, 'フォルダを作成するか map-list.json から削除'));
        }
    }
    for (const dirName of mapDirs) {
        if (!mapList.includes(dirName)) {
            findings.push(finding('warning', 1, `maps/${dirName}/`, '', `フォルダ maps/${dirName}/ が map-list.json に登録されていません`, 'map-list.json へ追加するかフォルダを削除'));
        }
    }

    // ルール 2: characters.startMapId
    const charactersJson = shared('characters.json');
    for (const [i, c] of (charactersJson?.characters ?? []).entries()) {
        if (c.startMapId && !mapList.includes(c.startMapId)) {
            findings.push(finding('error', 2, 'maps/_shared/characters.json', `characters[${i}].startMapId`, `startMapId「${c.startMapId}」が map-list.json にありません`));
        }
        // ルール 7: initialItems / initialEquipment
        for (const itemId of Object.keys(c.initialItems ?? {})) {
            if (!itemIds.has(itemId)) {
                findings.push(finding('error', 7, 'maps/_shared/characters.json', `characters[${i}].initialItems.${itemId}`, `initialItems のアイテム ID「${itemId}」が items.json にありません`));
            }
        }
        for (const [j, eq] of (c.initialEquipment ?? []).entries()) {
            if (eq?.equipmentId && !equipItemIds.has(eq.equipmentId) && !equipTemplateIds.has(eq.equipmentId)) {
                findings.push(finding('error', 7, 'maps/_shared/characters.json', `characters[${i}].initialEquipment[${j}]`, `装備 ID「${eq.equipmentId}」が equipment.json にありません`));
            }
        }
    }

    // マップ単位のチェック
    for (const mapId of mapDirs) {
        const mapDir = path.join(mapsDir, mapId);
        const metaPath = path.join(mapDir, 'meta.json');
        const meta = readJsonIfExists(metaPath);
        if (!meta) {
            findings.push(finding('error', 1, rel(metaPath), '', 'meta.json がありません（マップ読み込み自体が失敗します）'));
            continue;
        }
        // ルール 2: meta.player.deathRespawnMapId の参照先も map-list に存在すること
        //（AI 生成 meta.json が削除済みマップを指すと死亡時リスポーンで実行時エラーになる）
        const respawnId = meta.player?.deathRespawnMapId;
        if (respawnId && !mapList.includes(respawnId)) {
            findings.push(finding('error', 2, rel(metaPath), 'player.deathRespawnMapId', `deathRespawnMapId「${respawnId}」が map-list.json にありません`, '実在する mapId にするか、このキーを削除（開始マップへリスポーン）'));
        }
        if (meta.id !== mapId) {
            findings.push(finding('error', 1, rel(metaPath), 'id', `meta.id「${meta.id}」がフォルダ名「${mapId}」と一致しません`));
        }
        const areaIds = new Set((meta.areas ?? []).map((a) => a.id));

        const events = readJsonIfExists(path.join(mapDir, 'events.json'));
        if (events) {
            walkJson(events, ({ key, value, path: p, parent }) => {
                // ルール 3: map_warp 先の存在
                if (key === 'mapId' && parent?.type === 'map_warp') {
                    if (!mapList.includes(value)) {
                        findings.push(finding('error', 3, `maps/${mapId}/events.json`, p, `map_warp 先の mapId「${value}」が存在しません`, 'ワールド追加（F12）か参照の削除'));
                    }
                }
                // ルール 4: areaId
                if (key === 'areaId' && typeof value === 'string' && !areaIds.has(value)) {
                    findings.push(finding('error', 4, `maps/${mapId}/events.json`, p, `areaId「${value}」が meta.areas[] にありません`));
                }
                // BGM トラック参照
                if (key === 'track' && typeof value === 'string' && audioTracks.size > 0 && !audioTracks.has(value)) {
                    findings.push(finding('error', 10, `maps/${mapId}/events.json`, p, `BGM トラック「${value}」が audio.json にありません`));
                }
            });
        }

        const objects = readJsonIfExists(path.join(mapDir, 'objects.json'));
        if (objects) {
            walkJson(objects, ({ key, value, path: p, parent }) => {
                // ルール 5: lootTableId
                if (key === 'lootTableId' && typeof value === 'string' && !lootTableIds.has(value)) {
                    findings.push(finding('error', 5, `maps/${mapId}/objects.json`, p, `lootTableId「${value}」が loot-tables.json にありません`));
                }
                // ルール 3': 入口プロップの行き先
                if (key === 'destination' && typeof value === 'string' && parent?.type === 'entrance' && !mapList.includes(value)) {
                    findings.push(finding('error', 3, `maps/${mapId}/objects.json`, p, `入口プロップの行き先 mapId「${value}」が存在しません`));
                }
            });

            // ルール 11: type:"building" の buildingType が buildingTypeSettings / meta.defaultBuildingType に存在
            // （未定義だと BuildingGenerator がマップ入場時に throw する = 起動不能）
            const buildingTypeKeys = new Set(Object.keys(objects.buildingTypeSettings ?? {}));
            for (const [i, obj] of (objects.objects ?? []).entries()) {
                if (obj?.type !== 'building') continue;
                const buildingType = obj.buildingType ?? meta.defaultBuildingType;
                if (!buildingType) {
                    findings.push(finding('error', 11, `maps/${mapId}/objects.json`, `objects[${i}]`, `building「${obj.id ?? i}」に buildingType がなく meta.defaultBuildingType も未設定です`, 'objects[].buildingType か meta.defaultBuildingType を設定'));
                } else if (!buildingTypeKeys.has(buildingType)) {
                    findings.push(finding('error', 11, `maps/${mapId}/objects.json`, `objects[${i}].buildingType`, `buildingType「${buildingType}」が buildingTypeSettings にありません`, 'objects.json の buildingTypeSettings に定義を追加'));
                }
            }
        }

        const enemies = readJsonIfExists(path.join(mapDir, 'enemies.json'));
        if (enemies) {
            enemyDocuments.push(enemies);
            const localTypes = new Set(Object.keys(enemies.enemyTypes ?? {}));
            for (const [k, t] of Object.entries(enemies.enemyTypes ?? {})) {
                if (t?.configId && !enemyMasterIds.has(t.configId)) {
                    findings.push(finding('error', 6, `maps/${mapId}/enemies.json`, `enemyTypes.${k}.configId`, `敵マスタ ID「${t.configId}」が _shared/enemies.json にありません`));
                }
            }
            const usedTypes = new Set();
            const enemyIds = new Set();
            for (const [i, e] of (enemies.enemies ?? []).entries()) {
                if (typeof e?.id === 'string') {
                    if (enemyIds.has(e.id)) {
                        findings.push(finding('error', 13, `maps/${mapId}/enemies.json`, `enemies[${i}].id`, `敵ID「${e.id}」が重複しています`, '敵ごとに一意の id を設定'));
                    }
                    enemyIds.add(e.id);
                }
                if (e?.type) {
                    usedTypes.add(e.type);
                    if (!localTypes.has(e.type) && !enemyMasterIds.has(e.type)) {
                        findings.push(finding('error', 6, `maps/${mapId}/enemies.json`, `enemies[${i}].type`, `敵 type「${e.type}」が enemyTypes にも _shared/enemies.json にもありません`));
                    }
                }
                if (typeof e?.loot === 'string' && !lootTableIds.has(e.loot)) {
                    findings.push(finding('error', 5, `maps/${mapId}/enemies.json`, `enemies[${i}].loot`, `loot「${e.loot}」が loot-tables.json にありません`));
                }
            }
            for (const t of localTypes) {
                if (!usedTypes.has(t)) {
                    findings.push(finding('warning', 6, `maps/${mapId}/enemies.json`, `enemyTypes.${t}`, `enemyTypes「${t}」は定義されていますが未使用です`));
                }
            }
            if (Number.isFinite(Number(enemies.totalCount)) && Number(enemies.totalCount) !== (enemies.enemies ?? []).length) {
                findings.push(finding('error', 13, `maps/${mapId}/enemies.json`, 'totalCount', `totalCount=${enemies.totalCount} が enemies の件数 ${(enemies.enemies ?? []).length} と一致しません`, 'totalCount を enemies の実件数に合わせる'));
            }
        }

        // ルール 14: NPC AI人格チャット（npc-personas.json ⇔ npcs.json ⇔ characters.json）の整合。
        // 迷子ペルソナ（npcId 不在）・未知の variant キー・会話 0 件（話しかけ自体が発動しない）を検出する。
        const npcsJson = readJsonIfExists(path.join(mapDir, 'npcs.json'));
        const npcById = new Map((npcsJson?.npcs ?? []).filter((n) => typeof n?.id === 'string').map((n) => [n.id, n]));
        const playableIds = new Set((charactersJson?.characters ?? []).map((c) => c?.id).filter(Boolean));
        const npcPersonas = readJsonIfExists(path.join(mapDir, 'npc-personas.json'));
        if (npcPersonas) {
            for (const [npcId, entry] of Object.entries(npcPersonas.personas ?? {})) {
                const npc = npcById.get(npcId);
                if (!npc) {
                    findings.push(finding('error', 14, `maps/${mapId}/npc-personas.json`, `personas.${npcId}`, `ペルソナ対象の npcId「${npcId}」が npcs.json にありません`, 'npcs.json へ NPC を配置するかペルソナを削除'));
                    continue;
                }
                const variants = entry?.variants;
                if (variants && typeof variants === 'object') {
                    for (const variantKey of Object.keys(variants)) {
                        if (playableIds.size > 0 && !playableIds.has(variantKey)) {
                            findings.push(finding('error', 14, `maps/${mapId}/npc-personas.json`, `personas.${npcId}.variants.${variantKey}`, `variant キー「${variantKey}」が characters.json のプレイアブル ID にありません`, '選択キャラ id と一致させる'));
                        }
                    }
                    for (const playableId of playableIds) {
                        if (npc?.type === 'companion' && !variants[playableId]) {
                            findings.push(finding('error', 14, `maps/${mapId}/npc-personas.json`, `personas.${npcId}.variants`, `companion の variant にプレイアブル「${playableId}」がありません（そのキャラでプレイ時に AI 会話が無効になります）`, '全プレイアブル分の variant を定義'));
                        }
                    }
                }
                // 会話 0 件だと話しかけ（E キー）が発動せず AI 会話も開始できない
                const variantList = variants && typeof variants === 'object' ? Object.keys(variants) : [null];
                const hasDialogue = variantList.some((variantKey) => {
                    if (npc?.type === 'companion' && npc?.dialogueVariants && variantKey) {
                        return (npc.dialogueVariants[variantKey]?.dialogues ?? []).length > 0;
                    }
                    return (npc?.dialogues ?? []).length > 0;
                });
                if (!hasDialogue) {
                    findings.push(finding('error', 14, `maps/${mapId}/npcs.json`, `npcs(id=${npcId})`, `AI チャット対象 NPC「${npcId}」の dialogues が 0件です（話しかけが発動しません）`, 'dialogues（companion は各 variant の dialogues）へ 1件以上のセリフを設定'));
                }
            }
        }
        // companion NPC 自体の dialogueVariants キーもプレイアブル ID と一致すること
        for (const npc of npcById.values()) {
            if (npc?.type !== 'companion' || !npc?.dialogueVariants || typeof npc.dialogueVariants !== 'object') continue;
            for (const variantKey of Object.keys(npc.dialogueVariants)) {
                if (playableIds.size > 0 && !playableIds.has(variantKey)) {
                    findings.push(finding('error', 14, `maps/${mapId}/npcs.json`, `npcs(id=${npc.id}).dialogueVariants.${variantKey}`, `companion の variant キー「${variantKey}」が characters.json のプレイアブル ID にありません`, '選択キャラ id と一致させる'));
                }
            }
        }

        // ルール 9: hasShops と shops.json の有無
        const hasShopsFile = existsSync(path.join(mapDir, 'shops.json'));
        if (meta.hasShops === true && !hasShopsFile) {
            findings.push(finding('error', 9, `maps/${mapId}/meta.json`, 'hasShops', 'hasShops=true ですが shops.json がありません'));
        }
        if (meta.hasShops !== true && hasShopsFile) {
            findings.push(finding('warning', 9, `maps/${mapId}/shops.json`, '', 'shops.json がありますが meta.hasShops が true でないため読み込まれません'));
        }

        const shops = readJsonIfExists(path.join(mapDir, 'shops.json'));
        if (shops) {
            walkJson(shops, ({ key, value, path: p }) => {
                if (key === 'itemId' && typeof value === 'string' && !itemIds.has(value) && !equipItemIds.has(value)) {
                    findings.push(finding('error', 7, `maps/${mapId}/shops.json`, p, `アイテム ID「${value}」がマスタ（items / equipment）にありません`));
                } else if (key === 'itemId' && typeof value === 'string' && itemById.has(value)) {
                    const reason = forbiddenShopItemReason(itemById.get(value), mapList);
                    if (reason) {
                        findings.push(finding('error', 12, `maps/${mapId}/shops.json`, p, `販売禁止アイテム「${value}」がショップにあります: ${reason}`, '商品エントリを削除する'));
                    }
                }
                if (key === 'templateId' && typeof value === 'string' && !equipTemplateIds.has(value)) {
                    findings.push(finding('error', 7, `maps/${mapId}/shops.json`, p, `装備テンプレート ID「${value}」が equipment.json にありません`));
                }
                if (key === 'areaId' && typeof value === 'string' && !areaIds.has(value)) {
                    findings.push(finding('error', 4, `maps/${mapId}/shops.json`, p, `areaId「${value}」が meta.areas[] にありません`));
                }
            });
            for (const [i, prof] of (shops.shopProfiles ?? []).entries()) {
                for (const poolId of prof?.commonPoolIds ?? []) {
                    if (commonPoolIds.size > 0 && !commonPoolIds.has(poolId)) {
                        findings.push(finding('error', 7, `maps/${mapId}/shops.json`, `shopProfiles[${i}].commonPoolIds`, `commonPoolId「${poolId}」が shop-master.json にありません`));
                    }
                }
            }
        }
    }

    for (const enemyTypeId of unplacedStoryEnemyTypeIds(enemiesMaster, enemyDocuments)) {
        findings.push(finding(
            'error', 13, 'maps/_shared/enemies.json', `enemies.${enemyTypeId}`,
            `物語敵タイプ「${enemyTypeId}」が登録されていますが、どのマップにも配置されていません`,
            'ワールド設計の enemyPlacements を maps/<mapId>/enemies.json へ再反映',
        ));
    }

    // 全マップが参照する共通プールも同じ販売禁止契約で検査する。
    const commonPoolLists = Array.isArray(shopMaster?.commonPools)
        ? shopMaster.commonPools.flatMap((pool) => Array.isArray(pool?.entries) ? pool.entries : [])
        : Object.values(shopMaster?.commonPools ?? {}).flatMap((entries) => Array.isArray(entries) ? entries : []);
    for (const [i, entry] of commonPoolLists.entries()) {
        if (!entry?.itemId) continue;
        if (entry?.kind === 'equipment' && !itemById.has(entry.itemId)) continue;
        const reason = forbiddenShopItemReason(itemById.get(entry.itemId) ?? { id: entry.itemId }, mapList);
        if (reason) {
            findings.push(finding('error', 12, 'maps/_shared/shop-master.json', `commonPools.entries[${i}].itemId`, `販売禁止アイテム「${entry.itemId}」が共通商品プールにあります: ${reason}`, '共通商品エントリを削除する'));
        }
    }

    // ルール 7: 共有マスタ間の参照（loot-tables / exchange-shop）
    for (const [name, json] of [['loot-tables.json', lootJson], ['exchange-shop.json', shared('exchange-shop.json')]]) {
        if (!json) continue;
        walkJson(json, ({ key, value, path: p }) => {
            if (key === 'itemId' && typeof value === 'string' && !itemIds.has(value) && !equipItemIds.has(value)) {
                findings.push(finding('error', 7, `maps/_shared/${name}`, p, `アイテム ID「${value}」がマスタ（items / equipment）にありません`));
            }
            if (key === 'templateId' && typeof value === 'string' && !equipTemplateIds.has(value)) {
                findings.push(finding('error', 7, `maps/_shared/${name}`, p, `装備テンプレート ID「${value}」が equipment.json にありません`));
            }
        });
    }

    // ルール 8: nameKey / descriptionKey のロケール存在
    const checkLocaleKeys = (fileName, json) => {
        if (!json) return;
        walkJson(json, ({ key, value, path: p }) => {
            if ((key === 'nameKey' || key === 'descriptionKey') && typeof value === 'string') {
                for (const [lang, locale] of [['ja', jaLocale], ['en', enLocale]]) {
                    if (locale && getByDotPath(locale.messages ?? {}, value) === undefined) {
                        findings.push(finding('error', 8, `maps/_shared/${fileName}`, p, `${key}「${value}」が ${lang}.json の messages にありません`, `locales/${lang}.json へキーを追加`));
                    }
                }
            }
        });
    };
    checkLocaleKeys('items.json', itemsJson);
    checkLocaleKeys('equipment.json', equipmentJson);
    checkLocaleKeys('skills.json', skillsJson);

    // characterSkills のスキル ID 存在（未知 ID の緩い検査）
    const skillIds = new Set([
        ...(skillsJson?.passiveSkills ?? []).map((s) => s.id),
        ...(skillsJson?.activeSkills ?? []).map((s) => s.id),
    ]);
    for (const [charId, list] of Object.entries(skillsJson?.characterSkills ?? {})) {
        for (const sid of Array.isArray(list) ? list : []) {
            if (!skillIds.has(sid)) {
                findings.push(finding('error', 7, 'maps/_shared/skills.json', `characterSkills.${charId}`, `スキル ID「${sid}」が skills.json の定義にありません`));
            }
        }
    }

    const errors = findings.filter((f) => f.level === 'error');
    const result = { ok: errors.length === 0, errorCount: errors.length, warningCount: findings.length - errors.length, findings };
    updateGameMeta(gameId, (meta) => ({
        ...meta,
        lastValidation: { ...(meta.lastValidation ?? {}), integrity: { ok: result.ok, errors: result.errorCount, warnings: result.warningCount, at: new Date().toISOString() } },
    }));
    auditLog('validate-integrity', { gameId, errors: result.errorCount, warnings: result.warningCount });
    return result;
}
